import { useState } from "react"

import { Format, Money } from "../../classes/money"

const PARTIAL_INPUTS = [".", "-", "-."]

export interface UseMoneyInputProps {
  money?: Money
  onChangeMoney?: (m: Money) => void
  onBlurMoney?: (m: Money) => void
}

export function useMoneyInput({
  money,
  onChangeMoney,
  onBlurMoney,
}: UseMoneyInputProps) {
  const [moneyString, setMoneyString] = useState(
    money?.format(Format.NO_DOLLAR_SIGN) ?? ""
  )

  const [isEditing, setIsEditing] = useState(false)

  const value =
    !isEditing && money ? money.format(Format.NO_DOLLAR_SIGN) : moneyString

  const handleChange = (raw: string) => {
    const sanitized = sanitizeInput(raw)
    setMoneyString(sanitized)

    if (!onChangeMoney) return

    if (!sanitized || PARTIAL_INPUTS.includes(sanitized))
      return onChangeMoney(Money.zero())

    try {
      onChangeMoney(Money.fromDollars(sanitized))
    } catch {
      onChangeMoney(Money.zero())
    }
  }

  const handleBlur = () => {
    setIsEditing(false)
    const current = moneyString.trim()

    if (!current) {
      const fallback = money?.format(Format.NO_DOLLAR_SIGN) ?? ""
      setMoneyString(fallback)
      money && onBlurMoney?.(money)
      return
    }

    let parsed: Money

    try {
      parsed = Money.fromDollars(removeCommas(current))
    } catch {
      const fallback = money?.format(Format.NO_DOLLAR_SIGN) ?? ""
      setMoneyString(fallback)
      money && onBlurMoney?.(money)
      return
    }

    const final = parsed.format(Format.NO_DOLLAR_SIGN)
    setMoneyString(final)
    onBlurMoney?.(parsed)
  }

  const handleFocus = () => {
    setIsEditing(true)
    money && setMoneyString(money.format(Format.NO_DOLLAR_SIGN))
  }

  return {
    value,
    handleChange,
    handleBlur,
    handleFocus,
  }
}

/** Responsibilities:
 * 1. Preserve -'ve sign
 * 2. Keep only one decimal point
 * 3. Remove all other non-digit characters
 */
function sanitizeInput(raw: string): string {
  if (PARTIAL_INPUTS.includes(raw)) return raw

  const input = removeCommas(raw)

  const isNegative = input.startsWith("-")
  const abs = isNegative ? input.substring(1) : input

  const parts = abs.split(".")

  const integerPart = parts[0].split("").filter(isDigit).join("")

  let result = integerPart

  // Add decimal part
  if (parts.length > 1) {
    const decimalPart = parts[1].split("").filter(isDigit).join("")
    if (decimalPart || integerPart) result += "." + decimalPart
  }

  if (isNegative) result = "-" + result

  // Final validation
  if (!result || PARTIAL_INPUTS.includes(result)) return result
  return isNaN(Number(result)) ? "" : result
}

function isDigit(char: string) {
  return char >= "0" && char <= "9"
}

function removeCommas(str: string) {
  return str.split(",").join("")
}
